import React, { FC } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Icon,
  Divider,
  Button,
  alpha,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import { ProcessedItem } from "../../../services/processed-items.service";
import { toast } from "react-toastify";
import { processedItemsService } from "../../../services/processed-items.service";

interface ProcessedItemDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  processedItem: ProcessedItem;
}

const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
}> = ({ icon, label, value, valueColor }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        fontFamily: "Montserrat",
      }}
    >
      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        fontWeight: 500,
        color: valueColor || "text.primary",
        fontFamily: "Montserrat",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const ProcessedItemDetailsDrawer: FC<
  ProcessedItemDetailsDrawerProps
> = ({ open, onClose, processedItem }) => {
  const [loading, setLoading] = React.useState(false);

  const handleConsume = async () => {
    try {
      setLoading(true);
      await processedItemsService.consumeProcessedItem({
        _id: processedItem._id,
        quantity: 1,
        notes: "Consumed from details view",
      });
      toast.success("Item consumed successfully");
      onClose();
    } catch (error) {
      console.error("Error consuming item:", error);
      toast.error("Failed to consume item");
    } finally {
      setLoading(false);
    }
  };

  const handleWaste = async () => {
    try {
      setLoading(true);
      await processedItemsService.wasteProcessedItem({
        _id: processedItem._id,
        quantity: 1,
        notes: "Wasted from details view",
      });
      toast.success("Item marked as wasted");
      onClose();
    } catch (error) {
      console.error("Error marking item as wasted:", error);
      toast.error("Failed to mark item as wasted");
    } finally {
      setLoading(false);
    }
  };

  const isExpired = dayjs(processedItem.expiry_date).isBefore(dayjs());
  const isExpiring = dayjs(processedItem.expiry_date).isBefore(
    dayjs().add(7, "day")
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: "100%",
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          overflow: "hidden",
          borderLeft: "1px solid",
          borderColor: alpha("#0891B2", 0.1),
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Icon>lunch_dining</Icon>
          <Typography variant="subtitle1" fontWeight={600}>
            Processed Item Details
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Box sx={{ p: 3 }}>
        <Box display="grid" gap={3}>
          {/* Basic Information */}
          <DetailItem icon="tag" label="Item Name" value={processedItem.name} />
          {processedItem.description && (
            <DetailItem
              icon="description"
              label="Description"
              value={processedItem.description}
            />
          )}
          <DetailItem
            icon="category"
            label="Category"
            value={processedItem.category || "Uncategorized"}
          />
          <DetailItem
            icon="qr_code"
            label="Batch ID"
            value={processedItem.api_batch_id}
          />
          <DetailItem
            icon="inventory"
            label="Quantity"
            value={`${processedItem.remaining_quantity}/${processedItem.quantity} ${processedItem.unit}`}
            valueColor={
              processedItem.remaining_quantity === 0
                ? "#DC2626"
                : processedItem.remaining_quantity <
                  processedItem.quantity * 0.2
                ? "#F59E0B"
                : "#059669"
            }
          />
          <DetailItem
            icon="payments"
            label="Cost Information"
            value={`${processedItem.cost_per_unit.toFixed(3)} BHD per ${
              processedItem.unit
            } (Total: ${processedItem.total_cost.toFixed(3)} BHD)`}
          />
          <DetailItem
            icon="event"
            label="Production Date"
            value={dayjs(processedItem.production_date).format("MMM D, YYYY")}
          />
          <DetailItem
            icon="event"
            label="Expiry Date"
            value={dayjs(processedItem.expiry_date).format("MMM D, YYYY")}
            valueColor={
              isExpired ? "#DC2626" : isExpiring ? "#F59E0B" : undefined
            }
          />
          <DetailItem
            icon="fiber_manual_record"
            label="Status"
            value={processedItem.is_active ? "Active" : "Inactive"}
            valueColor={processedItem.is_active ? "#059669" : "#DC2626"}
          />
        </Box>

        {/* Ingredients Section */}
        {processedItem.ingredients && processedItem.ingredients.length > 0 && (
          <>
            <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: "text.primary",
                fontFamily: "Montserrat",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>restaurant</Icon>
              Ingredients Used
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: alpha("#0891B2", 0.04),
                border: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              {processedItem.ingredients.map((ingredient, index) => (
                <Box
                  key={index}
                  sx={{
                    "&:not(:last-child)": {
                      mb: 2,
                      pb: 2,
                      borderBottom: "1px solid",
                      borderColor: alpha("#0891B2", 0.1),
                    },
                  }}
                >
                  <DetailItem
                    icon="inventory_2"
                    label="Ingredient"
                    value={`${ingredient.quantity} ${
                      ingredient.unit
                    } (Cost: ${ingredient.cost.toFixed(3)} BHD)`}
                  />
                  <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                    <Chip
                      size="small"
                      icon={<Icon sx={{ fontSize: 16 }}>tag</Icon>}
                      label={`Batch: ${ingredient.batch_id.substring(0, 8)}`}
                      sx={{
                        bgcolor: alpha("#0891B2", 0.1),
                        color: "#0891B2",
                        fontWeight: 500,
                      }}
                    />
                    {ingredient.is_matching_recipe && (
                      <Chip
                        size="small"
                        icon={<Icon sx={{ fontSize: 16 }}>check_circle</Icon>}
                        label="Matches Recipe"
                        sx={{
                          bgcolor: alpha("#059669", 0.1),
                          color: "#059669",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Storage Information */}
        {(processedItem.storage_place ||
          processedItem.storage_instructions) && (
          <>
            <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                color: "text.primary",
                fontFamily: "Montserrat",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>place</Icon>
              Storage Information
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: alpha("#0891B2", 0.04),
                border: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              }}
            >
              {processedItem.storage_place && (
                <DetailItem
                  icon="place"
                  label="Storage Place"
                  value={processedItem.storage_place}
                />
              )}
              {processedItem.storage_instructions && (
                <Box sx={{ mt: 2 }}>
                  <DetailItem
                    icon="info"
                    label="Storage Instructions"
                    value={processedItem.storage_instructions}
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Consumption History */}
        {processedItem.consumption_logs &&
          processedItem.consumption_logs.length > 0 && (
            <>
              <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  color: "text.primary",
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon sx={{ fontSize: 20 }}>history</Icon>
                Consumption History
              </Typography>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha("#0891B2", 0.04),
                  border: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                }}
              >
                {processedItem.consumption_logs.map((log, index) => (
                  <Box
                    key={index}
                    sx={{
                      "&:not(:last-child)": {
                        mb: 2,
                        pb: 2,
                        borderBottom: "1px solid",
                        borderColor: alpha("#0891B2", 0.1),
                      },
                    }}
                  >
                    <DetailItem
                      icon={
                        log.action === "produce"
                          ? "add_circle"
                          : log.action === "consume"
                          ? "remove_circle"
                          : log.action === "waste"
                          ? "delete"
                          : "edit"
                      }
                      label={`${
                        log.action.charAt(0).toUpperCase() + log.action.slice(1)
                      } - ${log.quantity} ${processedItem.unit}`}
                      value={`${dayjs(log.timestamp).format(
                        "MMM D, YYYY HH:mm"
                      )} - Remaining: ${log.remaining_quantity} ${
                        processedItem.unit
                      }`}
                      valueColor={
                        log.action === "produce"
                          ? "#059669"
                          : log.action === "waste"
                          ? "#DC2626"
                          : undefined
                      }
                    />
                    {log.notes && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          mt: 0.5,
                          display: "block",
                        }}
                      >
                        Note: {log.notes}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        display: "block",
                      }}
                    >
                      Performed by: {log.performed_by}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

        {/* Actions */}
        <Divider sx={{ my: 3, borderColor: alpha("#0891B2", 0.1) }} />

        <Box display="flex" gap={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Icon>remove_circle</Icon>}
            onClick={handleConsume}
            disabled={loading || processedItem.remaining_quantity <= 0}
            sx={{
              borderColor: "#0891B2",
              color: "#0891B2",
              "&:hover": {
                borderColor: "#0E7490",
                bgcolor: alpha("#0891B2", 0.04),
              },
            }}
          >
            Consume
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<Icon>delete</Icon>}
            onClick={handleWaste}
            disabled={loading || processedItem.remaining_quantity <= 0}
            sx={{
              borderColor: "#DC2626",
              color: "#DC2626",
              "&:hover": {
                borderColor: "#B91C1C",
                bgcolor: alpha("#DC2626", 0.04),
              },
            }}
          >
            Mark as Waste
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
